import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";
import AuraMark from "@/components/brand/AuraMark";

const links = [
  { id: "home", label: "Home", testId: NAV.linkHome },
  { id: "tracks", label: "Event Tracks", testId: NAV.linkTracks },
  { id: "events", label: "Featured Events", testId: NAV.linkEvents },
  { id: "corporate", label: "Corporate & Private", testId: NAV.linkCorporate },
  { id: "how", label: "How It Works", testId: NAV.linkHow },
  { id: "partners", label: "Partners", testId: NAV.linkPartners },
  { id: "faq", label: "FAQ", testId: NAV.linkFaq },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        scrolled
          ? "bg-[var(--aura-ivory)]/85 border-b border-black/5"
          : "bg-[var(--aura-ivory)]/60 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => handleClick("home")}
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
          aria-label="Aura Events home"
        >
          <AuraMark size={32} />
          <span className="font-display text-xl md:text-2xl tracking-tight">
            Aura <span className="text-[var(--aura-teal)]">Events</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8 font-ui text-sm">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => handleClick(l.id)}
              data-testid={l.testId}
              className="text-[var(--aura-charcoal-2)] hover:text-[var(--aura-charcoal)] link-underline transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => handleClick("partners")}
            data-testid={NAV.ctaHost}
            className="font-ui text-sm px-4 py-2 rounded-full border border-[var(--aura-teal)]/25 text-[var(--aura-teal)] hover:bg-[var(--aura-teal)] hover:text-[var(--aura-ivory)] transition-colors"
          >
            Host With Aura
          </button>
          <button
            onClick={() => handleClick("events")}
            data-testid={NAV.ctaExplore}
            className="font-ui text-sm px-5 py-2.5 rounded-full bg-[var(--aura-teal)] text-[var(--aura-ivory)] hover:bg-[var(--aura-teal-hover)] transition-colors"
          >
            Explore Events
          </button>
        </div>

        <button
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-black/10"
          onClick={() => setOpen((o) => !o)}
          data-testid={NAV.mobileMenuBtn}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid={NAV.mobileMenu}
          className="lg:hidden bg-[var(--aura-ivory)] border-t border-black/5"
        >
          <div className="px-6 py-5 flex flex-col gap-4 font-ui">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => handleClick(l.id)}
                data-testid={`${l.testId}-mobile`}
                className="text-left text-[var(--aura-charcoal-2)] hover:text-[var(--aura-charcoal)]"
              >
                {l.label}
              </button>
            ))}
            <div className="pt-2 flex gap-3">
              <button
                onClick={() => handleClick("partners")}
                className="flex-1 text-sm px-4 py-2.5 rounded-full border border-[var(--aura-teal)]/25 text-[var(--aura-teal)]"
              >
                Host With Aura
              </button>
              <button
                onClick={() => handleClick("events")}
                className="flex-1 text-sm px-4 py-2.5 rounded-full bg-[var(--aura-teal)] text-[var(--aura-ivory)]"
              >
                Explore Events
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
